import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSettings } from '../../context/SettingsContext';

const fmt = (n) => (n != null && !isNaN(n) ? Number(n).toLocaleString() : '0');
const METHOD_ICONS = { cash: '💵', card: '💳', transfer: '🏦' };

function MonthStatusBadge({ status, t }) {
  if (status === 'paid')    return <span className="badge badge-green">✓ {t('paid')}</span>;
  if (status === 'partial') return <span className="badge" style={{ background: 'rgba(201,168,76,0.15)', color: 'var(--accent)' }}>◑ {t('partial')}</span>;
  return <span className="badge badge-red">✗ {t('unpaid')}</span>;
}

function RestaurantCalendarRow({ restaurant, t }) {
  const [calYear, setCalYear]   = useState(new Date().getFullYear());
  const [calendar, setCalendar] = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const navigate = useNavigate();

  const monthsShort = t('monthsShort');

  const loadCalendar = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/partner/restaurants/${restaurant._id}/calendar?year=${calYear}`);
      setCalendar(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [restaurant._id, calYear, open]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const calColor = (s) => {
    if (s === 'paid')    return { bg: 'var(--green-bg)',       border: 'rgba(76,175,130,0.25)',  dot: 'var(--green)'  };
    if (s === 'partial') return { bg: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.3)',   dot: 'var(--accent)' };
    if (s === 'overdue') return { bg: 'var(--red-bg)',         border: 'rgba(224,92,92,0.25)',   dot: 'var(--red)'    };
    return               { bg: 'var(--gray-bg)',       border: 'var(--border)',           dot: 'var(--text-dim)' };
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Restaurant header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500, fontSize: 15 }}>{restaurant.name}</span>
            {restaurant.planId && <span className="badge badge-gold">{restaurant.planId.name || restaurant.planId}</span>}
            <MonthStatusBadge status={restaurant.currentMonthStatus} t={t} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            {restaurant.phoneNumber && `📞 ${restaurant.phoneNumber}`}
            {restaurant.phoneNumber && restaurant.address && ' · '}
            {restaurant.address && `📍 ${restaurant.address}`}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 11px' }}
            onClick={e => { e.stopPropagation(); navigate(`/partner/restaurants/${restaurant._id}`); }}>
            {t('view')}
          </button>
          <span style={{ color: 'var(--text-dim)', fontSize: 18, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Calendar */}
      {open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px', background: 'var(--surface2)' }}>
          {/* Year nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <button className="btn-ghost" style={{ padding: '5px 11px', fontSize: 12 }} onClick={() => setCalYear(y => y - 1)}>← {calYear - 1}</button>
            <span style={{ fontFamily: 'DM Serif Display', fontSize: 18 }}>{calYear}</span>
            <button className="btn-ghost" style={{ padding: '5px 11px', fontSize: 12 }} onClick={() => setCalYear(y => y + 1)}>{calYear + 1} →</button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px 0', fontSize: 13 }}>{t('loading')}</div>
          ) : (
            <div className="calendar-grid">
              {calendar.map(({ month, status, subscription: sub }) => {
                const c = calColor(status);
                return (
                  <div key={month} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{monthsShort[month - 1]}</span>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.dot, display: 'inline-block', flexShrink: 0 }} />
                    </div>
                    {sub ? (
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 12, color: c.dot }}>{fmt(sub.paidAmount)} <span style={{ fontSize: 10 }}>UZS</span></div>
                        {sub.remainingAmount > 0 && <div style={{ fontSize: 10, color: 'var(--red)' }}>-{fmt(sub.remainingAmount)}</div>}
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>{METHOD_ICONS[sub.paymentMethod]}</div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: c.dot }}>
                        {status === 'overdue' ? t('overdue_cal') : t('noData_cal')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PartnerDashboard() {
  const { t }      = useSettings();
  const navigate   = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading,     setLoading]     = useState(true);

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get('/api/partner/restaurants');
      setRestaurants(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const paid    = restaurants.filter(r => r.currentMonthStatus === 'paid').length;
  const partial = restaurants.filter(r => r.currentMonthStatus === 'partial').length;
  const unpaid  = restaurants.filter(r => !r.currentMonthStatus || r.currentMonthStatus === 'none').length;
  const month   = t('monthsShort')[new Date().getMonth()];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('myRestaurants')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {paid} {t('paid')} · {partial} {t('partial')} · {unpaid} {t('unpaid')} — {month}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        {[
          { label: t('totalRestaurants'), value: restaurants.length, color: 'var(--accent)' },
          { label: t('paid'),    value: paid,    color: 'var(--green)'  },
          { label: t('partial'), value: partial, color: 'var(--accent)' },
          { label: t('unpaid'),  value: unpaid,  color: 'var(--red)'    },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 24, fontFamily: 'DM Serif Display', color: s.color, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
      ) : restaurants.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🏪</div>
          <div style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{t('noAssignedRestaurants')}</div>
          <button className="btn-primary" onClick={() => navigate('/partner/requests')}>+ {t('newRequest')}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {restaurants.map(r => (
            <RestaurantCalendarRow key={r._id} restaurant={r} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}
